import {
  FaMapMarkerAlt,
  FaRegClock,
  FaStar,
  FaPlane,
  FaUmbrellaBeach,
  FaPaw,
  FaGlobeAfrica
} from "react-icons/fa";
import { IoMdArrowDropright } from "react-icons/io";
import { AppContext } from "../../App";
import { useContext } from "react";

import imageHero from "../../assets/serengeti.jpg";
import imageDay1 from "../../assets/trips.jpg";
import imageDay2 from "../../assets/lions.jpg";
import imageDay3 from "../../assets/elephants.jpg";
import imageDay4 from "../../assets/waterfalls.jpg";
import imageDay5 from "../../assets/wild_plains.jpg";
import imageDay6 from "../../assets/camp.jpg";
import imageDay7 from "../../assets/pumas.jpg";
import imageDay8 from "../../assets/zanz_excursion.jpg";
import imageDay9 from "../../assets/vultures.JPG";
import imageDay10 from "../../assets/zanzibar1.jpg";

/* ================= HERO ================= */
const hero = {
  kicker: "15 DAYS • TANZANIA + ZANZIBAR",
  title: "Ultimate Bush to Beach",
  subtitle: "From Big Five safaris to turquoise Indian Ocean escape.",
  backgroundImage: imageHero,
  meta: ["15 Days", "Big Five", "Serengeti", "Zanzibar Luxury"],
};

/* ================= THEME ================= */
const theme = {
  primary: "#92400e",      // Safari brown
  primaryLight: "#d97706", // Amber
  ocean: "#0ea5e9",        // Zanzibar blue
  bg: "#fffbeb",
  cardBg: "#ffffff",
  text: "#1e293b",
  textMuted: "#64748b"
};

/* ================= OVERVIEW ================= */
const overview = `A complete Tanzania journey combining iconic wildlife safaris across northern parks with a luxury beach escape in Zanzibar. Experience the raw African wilderness, then unwind on white-sand shores.`;

/* ================= HIGHLIGHTS ================= */
const highlights = [
  {
    icon: FaPaw,
    title: "Big Five Safari",
    description: "Track lions, elephants, leopards, rhinos, and buffalo.",
    color: "#92400e"
  },
  {
    icon: FaGlobeAfrica,
    title: "Ngorongoro Wonder",
    description: "Dense wildlife ecosystem inside a volcanic crater.",
    color: "#16a34a"
  },
  {
    icon: FaPlane,
    title: "Seamless Transition",
    description: "Fly from safari plains to Zanzibar coast.",
    color: "#6366f1"
  },
  {
    icon: FaUmbrellaBeach,
    title: "Zanzibar Escape",
    description: "White sands, coral reefs, and cultural Stone Town.",
    color: "#0ea5e9"
  },
];

/* ================= DETAILS ================= */
const details = [
  {
    icon: FaMapMarkerAlt,
    title: "Destinations",
    description: "Arusha, Tarangire, Serengeti, Ngorongoro, Zanzibar"
  },
  {
    icon: FaRegClock,
    title: "Duration",
    description: "15 days full experience"
  },
  {
    icon: FaStar,
    title: "Experience",
    description: "Luxury lodges + beach resorts"
  },
  {
    icon: FaPlane,
    title: "Transport",
    description: "Private safari + domestic flight"
  },
];

/* ================= ITINERARY ================= */
const itinerary = [
  {
    day: "01",
    phase: "Safari",
    title: "Arrival in Arusha",
    image: imageDay1,
    body: "Arrival, private transfer, lodge check-in.",
    tags: ["Arrival", "Rest"]
  },
  {
    day: "03",
    phase: "Safari",
    title: "Arusha National Park",
    image: imageDay3,
    body: "Game drive with rare primates and birdlife.",
    tags: ["Game Drive", "Wildlife"]
  },
  {
    day: "05",
    phase: "Safari",
    title: "Tarangire Elephants",
    image: imageDay5,
    body: "Massive elephant herds and baobab landscapes.",
    tags: ["Elephants", "Baobabs"]
  },
  {
    day: "06–08",
    phase: "Safari",
    title: "Serengeti Experience",
    image: imageDay6,
    body: "Predators, migration, endless plains.",
    tags: ["Lions", "Migration"]
  },
  {
    day: "09",
    phase: "Safari",
    title: "Ngorongoro Crater",
    image: imageDay7,
    body: "World’s densest wildlife concentration.",
    tags: ["Crater", "Big Five"]
  },
  {
    day: "10",
    phase: "Transition",
    title: "Fly to Zanzibar",
    image: imageDay8,
    body: "From savannah to tropical island.",
    tags: ["Flight", "Transfer"]
  },
  {
    day: "11–15",
    phase: "Beach",
    title: "Zanzibar Retreat",
    image: imageDay10,
    body: "Relaxation, ocean activities, cultural tours.",
    tags: ["Beach", "Snorkeling", "Stone Town"]
  },
];

/* ================= COMPONENT ================= */
export default function TanzaniaZanzibarSafari() {
  const appContext = useContext(AppContext);

  return (
    <div style={{ maxWidth: "1400px", margin: "0 auto", background: theme.bg }}>

      {/* HERO */}
      <section
        style={{
          height: "75vh",
          backgroundImage: `linear-gradient(135deg, rgba(20,10,5,0.85), rgba(14,165,233,0.6)), url(${hero.backgroundImage})`,
          backgroundSize: "cover",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "white",
          textAlign: "center"
        }}
      >
        <div>
          <span>{hero.kicker}</span>
          <h1 style={{ fontSize: "3.5rem", margin: "1rem 0" }}>{hero.title}</h1>
          <p style={{ fontSize: "1.3rem" }}>{hero.subtitle}</p>

          <div style={{ marginTop: "1.5rem", display: "flex", gap: "0.5rem", justifyContent: "center", flexWrap: "wrap" }}>
            {hero.meta.map((m, i) => (
              <span key={i} style={{
                background: "rgba(255,255,255,0.2)",
                padding: "0.4rem 1rem",
                borderRadius: "20px"
              }}>
                {m}
              </span>
            ))}
          </div>
        </div>
      </section>

      <div style={{ padding: "4rem 2rem" }}>

        {/* OVERVIEW */}
        <section style={{ textAlign: "center", marginBottom: "4rem" }}>
          <h2>The Complete Tanzania Experience</h2>
          <p style={{ maxWidth: "700px", margin: "auto" }}>{overview}</p>
        </section>

        {/* DETAILS */}
        <section style={{ display: "flex", gap: "1rem", flexWrap: "wrap", marginBottom: "4rem" }}>
          {details.map((d, i) => {
            const Icon = d.icon;
            return (
              <div key={i} style={{ background: "white", padding: "1rem", borderRadius: "10px", display: "flex", gap: "0.7rem" }}>
                <Icon color={theme.primary} />
                <div>
                  <strong>{d.title}</strong>
                  <div>{d.description}</div>
                </div>
              </div>
            );
          })}
        </section>

        {/* HIGHLIGHTS */}
        <section style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(250px,1fr))", gap: "1rem" }}>
          {highlights.map((h, i) => {
            const Icon = h.icon;
            return (
              <div key={i} style={{ background: "white", padding: "1.5rem", borderRadius: "12px" }}>
                <Icon style={{ color: h.color }} />
                <h3>{h.title}</h3>
                <p>{h.description}</p>
              </div>
            );
          })}
        </section>

        {/* ITINERARY TIMELINE */}
        <section style={{ marginTop: "4rem" }}>
          <h2 style={{ textAlign: "center" }}>Journey Timeline</h2>

          {itinerary.map((day, i) => (
            <div key={i} style={{
              display: "flex",
              flexDirection: i % 2 ? "row-reverse" : "row",
              margin: "2rem 0",
              background: "white",
              borderRadius: "12px",
              overflow: "hidden"
            }}>
              <div style={{
                width: "40%",
                backgroundImage: `url(${day.image})`,
                backgroundSize: "cover"
              }} />
              <div style={{ padding: "1.5rem", width: "60%" }}>
                <span style={{
                  fontSize: "0.8rem",
                  background: day.phase === "Beach" ? theme.ocean : theme.primary,
                  color: "white",
                  padding: "0.2rem 0.6rem",
                  borderRadius: "10px"
                }}>
                  {day.phase}
                </span>

                <h3>Day {day.day}: {day.title}</h3>
                <p>{day.body}</p>
              </div>
            </div>
          ))}
        </section>

        {/* CTA */}
        <section style={{ textAlign: "center", marginTop: "4rem" }}>
          <h2>Your Dream Safari + Beach Escape Awaits</h2>
          <button style={{
            background: `linear-gradient(135deg, ${theme.primary}, ${theme.ocean})`,
            color: "white",
            padding: "1rem 2rem",
            borderRadius: "30px",
            border: "none"
          }}
          onClick={()=>appContext.navTo({item:'plan-trip'})}>
            Plan This Journey <IoMdArrowDropright />
          </button>
        </section>

      </div>
    </div>
  );
}